import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { PODIUM_BLOCK_COLORS, PODIUM_GLOW, PODIUM_TOGGLE_COLORS } from './PodiumStage.constants';

const SIDE_FACE_WIDTH = 9;

export const getPodiumStageStyles = (theme: Theme) =>
  StyleSheet.create({
    root: {
      paddingHorizontal: 18,
      paddingTop: 12,
      paddingBottom: 8,
    },
    toggleRow: {
      alignSelf: 'center',
      flexDirection: 'row',
      gap: 4,
      padding: 4,
      borderRadius: 999,
      backgroundColor: PODIUM_TOGGLE_COLORS.rowBg,
      borderWidth: 1,
      borderColor: theme.gray[200] + '22',
      marginBottom: 12,
    },
    togglePill: {
      paddingHorizontal: 18,
      paddingVertical: 5,
      borderRadius: 999,
    },
    togglePillActive: {
      backgroundColor: theme.accent,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 4px 10px ${theme.accent}55` as never,
      }),
    },
    togglePillInactive: {
      backgroundColor: 'transparent',
    },
    toggleLabel: {
      fontSize: 12,
      fontWeight: '800',
      letterSpacing: 0.3,
    },
    toggleLabelActive: {
      color: PODIUM_TOGGLE_COLORS.labelActive,
    },
    toggleLabelInactive: {
      color: theme.gray[400],
    },

    /* Top-3 row */
    playersRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginTop: 4,
      paddingHorizontal: 4,
    },
    playerCol: {
      flex: 1,
      alignItems: 'center',
    },
    /** Staircase effect: 1st stands tallest, 2nd lower, 3rd lowest — matches block heights below. */
    playerColRank2: {
      transform: [{ translateY: 22 }],
    },
    playerColRank3: {
      transform: [{ translateY: 36 }],
    },
    playerPressed: {
      opacity: 0.85,
    },
    /* Avatar + glow halo */
    medallionWrap: {
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
    },
    glowHalo: {
      position: 'absolute',
      borderRadius: 999,
      opacity: 0.55,
      ...(Platform.OS === 'web' && {
        filter: 'blur(8px)' as never,
      }),
    },
    glowHaloFirst: {
      backgroundColor: PODIUM_GLOW.first + '55',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 24px 6px ${PODIUM_GLOW.first}55` as never,
      }),
    },
    glowHaloSecond: {
      backgroundColor: PODIUM_GLOW.second + '55',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 22px 4px ${PODIUM_GLOW.second}55` as never,
      }),
    },
    glowHaloThird: {
      backgroundColor: PODIUM_GLOW.third + '55',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 22px 4px ${PODIUM_GLOW.third}55` as never,
      }),
    },
    playerNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    playerName: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.gray[900],
      maxWidth: 104,
      textAlign: 'center',
    },
    playerNameMe: {
      color: theme.primary,
    },
    playerSlot: {
      flex: 1,
      alignItems: 'center',
    },
    blockGradientFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    playerPointsRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: 4,
      marginTop: 3,
    },
    playerPoints: {
      fontSize: 14,
      fontWeight: '800',
      color: theme.gray[900],
      letterSpacing: -0.3,
    },
    ptsLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.gray[500],
      letterSpacing: 0.4,
    },

    /* Iso 3D podium blocks */
    blocksRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginTop: 8,
      paddingHorizontal: 4,
    },
    blockCol: {
      flex: 1,
      alignItems: 'center',
    },
    block: {
      position: 'relative',
      width: 64,
    },
    blockFace: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow:
          'inset 0 2px 0 rgba(255,255,255,0.45), inset 0 -10px 0 rgba(0,0,0,0.15)' as never,
      }),
    },
    blockFaceFirstGlow: {
      ...(Platform.OS === 'web' && {
        boxShadow:
          `0 0 28px ${PODIUM_GLOW.first}33, inset 0 2px 0 rgba(255,255,255,0.45), inset 0 -10px 0 rgba(0,0,0,0.15)` as never,
      }),
    },
    blockSide: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: SIDE_FACE_WIDTH,
      right: -SIDE_FACE_WIDTH,
      transform: [{ skewY: '-26deg' }],
    },
    blockSideFirst: {
      backgroundColor: PODIUM_BLOCK_COLORS.first.side,
    },
    blockSideRest: {
      backgroundColor: PODIUM_BLOCK_COLORS.rest.side,
    },
    blockEdge: {
      position: 'absolute',
      top: -3,
      left: 4,
      /** width(64) - 4 + SIDE_FACE_WIDTH(9) - 4 = 65; encoded so the top face extends past the right side slope. */
      width: 64 - 4 + SIDE_FACE_WIDTH - 4,
      height: 6,
      borderTopLeftRadius: 3,
      borderTopRightRadius: 3,
      transform: [{ skewX: '-26deg' }],
    },
    blockEdgeFirst: {
      backgroundColor: PODIUM_BLOCK_COLORS.first.edge,
    },
    blockEdgeRest: {
      backgroundColor: PODIUM_BLOCK_COLORS.rest.edge,
    },
    blockNumeralColorFirst: {
      color: PODIUM_BLOCK_COLORS.first.numeralColor,
    },
    blockNumeralColorRest: {
      color: PODIUM_BLOCK_COLORS.rest.numeralColor,
    },
    blockNumeral: {
      fontWeight: '900',
      letterSpacing: -3,
      lineHeight: 48,
    },
    blockNumeralFirst: {
      fontSize: 48,
    },
    blockNumeralRest: {
      fontSize: 36,
      lineHeight: 36,
    },
    // ── Prize row (below podium blocks) ─────────────────────────────────────
    prizeRow: {
      flexDirection: 'row',
      marginTop: 10,
    },
    prizeCol: {
      flex: 1,
      alignItems: 'center',
      gap: 5,
    },
    prizeImage: {
      width: 52,
      height: 52,
      borderRadius: 8,
    },
    prizeLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: '#ccc',
      textAlign: 'center',
      paddingHorizontal: 4,
      lineHeight: 14,
    },
  });

/** Width of the right-side iso-skewed face on each podium block, in px. */
export const PODIUM_BLOCK_SIDE_WIDTH = SIDE_FACE_WIDTH;
